// ./src/components/features/Artists/ArtistSelect/ArtistSelect.tsx
//
// Picks a catalog Artist (bookable act) to book onto an event. SERVER-SEARCH-backed:
// typing runs an account-scoped (event-scoped for crew, via contextEventId) `searchArtists`
// lookup, so acts beyond the loaded first page are findable. Also lets the user create an
// artist in place via the real ArtistEditDrawer. On pick it hands back the whole Artist so
// the booking drawer can copy its name / deal / photo. Mirrors InventoryItemSelect.
//
// Self-contained: wraps its own <ArtistsProvider> (scoped to the event) so the search hook
// AND the create-in-place drawer share one provider — the booking drawer that hosts this
// picker mounts an EventArtistsProvider, not an ArtistsProvider.
//
// ⚠ THE CREATE-IN-PLACE IS PLAN-GATED, AND THE GATE LIVES HERE RATHER THAN ON THE PAGES.
// `ArtistCore.beforeCreate` refuses without `commercial`, but BOOKING an act
// (`EventArtistCore`) is not gated at all — so the pages that host this picker
// (EventArtistsPage, EventSchedulePage's slot drawer) must NOT carry a page-level notice:
// their own create path is allowed. It is only this one action inside them that the server
// refuses. Gating it in the shared picker fixes every host at once and cannot be forgotten
// by the next one.
//
// ⚠ IT DOES NOT NAVIGATE TO THE UPGRADE PAGE. This picker lives inside a half-filled booking
// drawer, and sending the host to Subscription from there would destroy what they had typed —
// the same mistake the first top-up drawer made, which the owner rejected. So the action is
// withdrawn and the picker EXPLAINS itself instead, in the server's own words.

import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Loader } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { FEATURE_CODES } from "@/config";
import { Artist, WHOLE_SET } from "@/utils/api";
import { ArtistsProvider, useArtistsContext } from "@/contexts";
import { useCreateGate } from "@/hooks/useCreateGate";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_FOR_EVENT_OPERATORS } from "@/utils/createInPlace";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

import { ArtistEditDrawer } from "../ArtistEditDrawer/ArtistEditDrawer";

export interface ArtistSelectProps {
  value: string | null;
  onChange: (artist: Artist | null) => void;
  /** The event this picker is shown on — scopes the catalog for cross-account crew. */
  eventId?: string | null;
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: React.ReactNode;
  /** A readable label for a preselected value not yet in the loaded/searched set. */
  fallbackLabel?: string | null;
}

const SEARCH_DEBOUNCE_MS = 250;

const ArtistSelectInner: React.FC<ArtistSelectProps> = ({
  value,
  onChange,
  label,
  description,
  placeholder,
  disabled,
  error,
  fallbackLabel,
  eventId,
}) => {
  const { t } = useTranslation(["artists", "common"]);
  const { artists, loading, searchArtists, get } = useArtistsContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // ⚠ The SAME feature the catalog page gates on — `ArtistCore` checks `commercial` in
  // `beforeCreate`, so this is the one resolver answering the one question, not a second rule.
  const createGate = useCreateGate(FEATURE_CODES.COMMERCIAL, { eventId });
  // #142 — the plan of the account the act lands in (the event's), and a crew member only with `event.operate`.
  const canCreate = useCreateInPlace(CREATE_FOR_EVENT_OPERATORS, FEATURE_CODES.COMMERCIAL, eventId);

  // Controlled search → server lookup (debounced). Empty query falls back to the loaded list.
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Artist[]>([]);
  const [searching, setSearching] = useState(false);
  // The selected act resolved by id (for an existing booking whose act isn't in the loaded page).
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const q = query.trim();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!q) {
      setResults([]);
      setSearching(false);

      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const rows = await searchArtists(q);
      setResults(rows);
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchArtists]);

  // Resolve the selected value's real label when it isn't among the loaded / searched acts.
  useEffect(() => {
    if (!value) {
      setSelectedArtist(null);

      return;
    }

    const known = artists.find((a) => a.id === value) || results.find((a) => a.id === value);
    if (known) {
      setSelectedArtist(known);

      return;
    }

    let cancelled = false;
    get(value).then((result) => {
      if (!cancelled && result.didSucceed()) {
        setSelectedArtist(result.getDataFirst() as Artist);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [value, artists, results, get]);

  const data = useMemo(() => {
    const base = query.trim() ? results : artists;
    const opts = base
      .filter((a) => !!a.id)
      .map((a) => ({
        value: a.id as string,
        label: a.name || t("artists:artist.untitled", { defaultValue: "Untitled act" }),
      }));

    if (value && !opts.some((o) => o.value === value)) {
      opts.unshift({
        value,
        label: selectedArtist?.name || fallbackLabel || t("artists:artist.selected", { defaultValue: "Selected act" }),
      });
    }

    return opts;
  }, [query, results, artists, value, selectedArtist, fallbackLabel, t]);

  const handleChange = useCallback(
    (id: string | null) => {
      if (!id) {
        onChange(null);

        return;
      }

      const artist =
        results.find((a) => a.id === id) ||
        artists.find((a) => a.id === id) ||
        (selectedArtist?.id === id ? selectedArtist : null);
      onChange(artist ?? null);
    },
    [results, artists, selectedArtist, onChange]
  );

  const handleCreated = (artist: Artist) => {
    setDrawerOpen(false);
    onChange(artist);
    setQuery(artist.name ?? "");
  };

  return (
    <>
      <SelectField
        label={label ?? t("artists:artist.label", { defaultValue: "Act" })}
        description={description}
        placeholder={
          placeholder ??
          (loading
            ? t("common:loading", { defaultValue: "Loading…" })
            : t("artists:artist.placeholder", { defaultValue: "Search your acts…" }))
        }
        data={data}
        value={value}
        onChange={(_n, id) => handleChange(id)}
        searchable
        searchValue={query}
        onSearchChange={setQuery}
        filter={({ options }) => options}
        onDropdownOpen={() => setQuery("")}
        searchLoading={searching}
        createLabel={
          canCreate ? t("artists:artist.create", { defaultValue: "Add a new act" }) : undefined
        }
        onCreate={canCreate ? () => setDrawerOpen(true) : undefined}
        clearable
        disabled={disabled}
        error={error}
        nothingFoundMessage={
          searching
            ? t("common:loading", { defaultValue: "Loading…" })
            : // ⚠ THE REFUSAL IS SAID, NOT JUST OBEYED. Withdrawing the create action without a
              // word is the failure mode this whole item exists to end — an empty picker with no
              // way to fill it and no reason given is indistinguishable from a bug. The verdict
              // carries the server's own sentence, so the host reads the same thing here as they
              // would from a save.
              createGate.blockedTooltip ||
              t("artists:artist.empty", { defaultValue: "No acts yet — add one" })
        }
        leftSection={<AppIcon iconName="Microphone" size={16} />}
        rightSection={searching || loading ? <Loader size="sm" /> : undefined}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
        size="md"
      />

      <ArtistEditDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} onCreated={handleCreated} />
    </>
  );
};

export const ArtistSelect: React.FC<ArtistSelectProps> = (props) => (
  // autoLoad: load the WHOLE roster (WHOLE_SET, no cap) so the dropdown shows every act on
  // open (empty query → the loaded list); typing still server-searches (ROADMAP #118).
  <ArtistsProvider pageSize={WHOLE_SET} eventId={props.eventId ?? null} autoLoad>
    <ArtistSelectInner {...props} />
  </ArtistsProvider>
);

export default ArtistSelect;
